import type { TcEvent } from "@theconversation/shared-components/tcEventsCollector"
import { userIsWebCrawler } from "@theconversation/shared-components/tcEventsCollector"
import { isDevelopment } from "common/lib/environments"
import getConfig from "next/config"

const eventQueue: Array<TcEvent> = []
const BATCH_WAIT_TIME_MS = 100
const MAX_BATCH_SIZE = 15
let endOfSessionDispatcherCreated: boolean = false
let userEventCollectorUrlValue

function userEventCollectorUrl(): string {
  userEventCollectorUrlValue ||= getConfig().publicRuntimeConfig.USER_EVENT_COLLECTOR_URL
  return userEventCollectorUrlValue
}

/*
  Public function to send an event.
  Unless the "immediate" flag is set, the event is held in a queue for a short
  period, in the hope that other events can be collected and then sent as a single batch.
*/
export function dispatchEvent(event: TcEvent, immediate: boolean = false) {
  if (userIsWebCrawler()) return

  eventQueue.push(event)

  ensureEndOfSessionDispatchHandler()

  if (eventQueue.length >= MAX_BATCH_SIZE || immediate) postQueuedEventsToCollector()
  else setTimeout(postQueuedEventsToCollector, BATCH_WAIT_TIME_MS)
}

/*
  Attempt to send any queued events to the collector service.
  Typically called in three ways -- either via a short timeout after
  dispatchEvent() was called, or else externally when a user's
  eligibility has changed (eg. through Didomi consent), or when the
  use has navigated away from the page (visibilitychange handler).
*/
export function postQueuedEventsToCollector() {
  const eventsToSend: Array<TcEvent> = []

  while (eventQueue.length > 0) {
    // shift() should never return undefined because we just checked the array was non-zero.
    // However in order to keep the linter happy, we do this dance:
    const e = eventQueue.shift()
    e && eventsToSend.push(e)
  }

  if (eventsToSend.length > 0) postEventsToCollector(eventsToSend)
}

/*
  Ensures an end-of-session dispatch handler exists.
  This is to catch the case where a user navigates away from the page while there were
  pending events that haven't been sent yet. It's a small window, but can
  occur if clicking a button both enqueues an event and also navigates away.
*/
function ensureEndOfSessionDispatchHandler() {
  if (!endOfSessionDispatcherCreated) {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        postQueuedEventsToCollector()
      }
    })
    endOfSessionDispatcherCreated = true
  }
}

/*
  Make an HTTP request to send an array of events to the collector.
  This function is private as callers are expected to use the public functions
  which test for eligibility and merge events into batches.
*/
function postEventsToCollector(events: Array<TcEvent>) {
  try {
    const eventUrl = userEventCollectorUrl() + "/v1/events"
    if (isDevelopment()) {
      events.forEach((event) =>
        console.log("Dispatching user analytics event, type: " + event.eventType),
      )
    }

    const blob = new Blob([JSON.stringify(events)], { type: "application/json" })
    navigator.sendBeacon(eventUrl, blob)
  } catch (err) {
    console.warn("Failed to dispatch events: " + err)
  }
}
